"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Alert } from "../../elements/alert/alert";
import { Button } from "../../elements/button/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../elements/dialog/dialog";
import { Label } from "../../elements/label/label";
import { RadioGroup } from "../../elements/radio-group/radio-group";
import { Text } from "../../elements/text/text";
import { useDebouncedCallback } from "../../hooks/use-debounced-callback";
import { UserFormField } from "./user-form-field";
import {
  createUserFormModalSchema,
  type UserFormModalData,
  type UserFormModalValidationMessages,
} from "./user-form-modal.schema";

const EMAIL_AVAILABILITY_CHECK_DELAY_MS = 500;

export interface UserFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  microcopies: {
    addTitle: string;
    addDescription: string;
    editTitle: string;
    editDescription: string;
    customerIdLabel: string;
    firstNameLabel: string;
    lastNameLabel: string;
    emailLabel: string;
    confirmEmailLabel: string;
    roleLabel: string;
    saveButtonLabel: string;
    serverErrorTitle?: string;
    /** Body text paired with `serverErrorTitle`, e.g. "The email you entered is already associated with another account." Customer ID conflicts use `customerIdServerErrorMessage`. */
    serverErrorMessage?: string;
    /** Title shown in the warning alert when Customer ID is already in use */
    customerIdServerErrorTitle?: string;
    /** Body text shown in the warning alert when Customer ID is already in use */
    customerIdServerErrorMessage?: string;
    /** Edit-mode-only title for an email conflict, e.g. "This email is already taken". Falls back to `serverErrorTitle` when omitted. */
    emailAlreadyTakenTitle?: string;
    /** Edit-mode-only body text for an email conflict, e.g. "Please use another email address". Falls back to `serverErrorMessage` when omitted. */
    emailAlreadyTakenMessage?: string;
  } & Partial<UserFormModalValidationMessages>;
  roleOptions: { value: string; label: string; description?: string }[];
  defaultValues?: Partial<UserFormModalData>;
  serverError?: string; // e.g. "Email already taken. You can't change your email to this one. It is already taken"
  customerIdServerError?: string; // e.g. "Customer ID already in use"
  /** Required: every instance of this modal (including own-profile, which is always edit-only) needs an edit path. */
  onEdit: (userId: string, data: UserFormModalData) => Promise<void> | void;
  /** Optional: only add-capable instances (e.g. My Company) supply this. Omitting it while in add mode throws on submit — see the guard in `onSubmit` below. */
  onAdd?: (data: UserFormModalData) => Promise<void> | void;
  isPending?: boolean;
  /** When false the Role field is hidden. Use for own-profile edits where role is not relevant. Defaults to true. */
  showRoleField?: boolean;
  /**
   * When provided, the email field is checked (debounced) as the admin types
   * — in both add and edit mode, there's no add/edit gating here. If it
   * resolves to `false` (already in use), the same warning Alert used for
   * `serverError` is shown below the email/confirm-email row and Save is
   * disabled. Not called when the value matches `defaultValues.email`
   * (unchanged) — that's edit-mode only, since add mode has no original
   * email to compare against. Called with `defaultValues.customerId` as the
   * second argument (the sub-account's own uid, so the caller can exclude it
   * server-side); `undefined` in add mode.
   */
  onCheckEmailAvailability?: (
    email: string,
    excludeUid?: string
  ) => Promise<boolean>;
}

function isValidEmailFormat(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function UserFormModal(props: Readonly<UserFormModalProps>) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<UserFormModalData>({
    // biome-ignore lint/suspicious/noExplicitAny: -
    resolver: zodResolver(createUserFormModalSchema(props.microcopies) as any),
    values: {
      customerId: props.defaultValues?.customerId ?? "",
      firstName: props.defaultValues?.firstName ?? "",
      lastName: props.defaultValues?.lastName ?? "",
      email: props.defaultValues?.email ?? "",
      confirmEmail: props.defaultValues?.confirmEmail ?? "",
      // No fallback: nothing pre-selected in add mode (see the modal schema).
      role: props.defaultValues?.role,
    },
    mode: "onChange",
  });

  // Server errors from a submit-time conflict are shown as a banner/alert via
  // `props.serverError`, kept as a fallback for races. The live check below
  // drives the same Alert (below the email/confirm-email row) as the admin
  // types, instead of a separate field-level error. Runs for both add and
  // edit mode — the `isEmailUnchanged` skip below is a perf optimization
  // (fewer network calls), not what makes an unchanged edit-mode email
  // correctly report as available; `excludeUid` (passed to the callback)
  // does that server-side.

  const emailValue = watch("email");
  const [isEmailTaken, setIsEmailTaken] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const checkRequestIdRef = useRef(0);

  const isEmailUnchanged = (value: string) => {
    const originalEmail = props.defaultValues?.email?.trim().toLowerCase();
    return !!originalEmail && value.trim().toLowerCase() === originalEmail;
  };

  const debouncedCheckEmail = useDebouncedCallback(async (email: string) => {
    if (!isValidEmailFormat(email) || isEmailUnchanged(email)) {
      return;
    }
    const requestId = checkRequestIdRef.current + 1;
    checkRequestIdRef.current = requestId;
    setIsCheckingEmail(true);
    const available = await props.onCheckEmailAvailability?.(
      email,
      props.defaultValues?.customerId
    );
    if (requestId !== checkRequestIdRef.current) {
      return;
    }
    setIsCheckingEmail(false);
    setIsEmailTaken(available === false);
  }, EMAIL_AVAILABILITY_CHECK_DELAY_MS);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when the email value changes; the other dependencies are stable helpers/props read at call time
  useEffect(() => {
    if (!props.onCheckEmailAvailability) {
      return;
    }
    const trimmedEmail = emailValue?.trim() ?? "";

    if (
      !trimmedEmail ||
      isEmailUnchanged(trimmedEmail) ||
      !isValidEmailFormat(trimmedEmail)
    ) {
      // Invalidate any in-flight/pending check so a late response can't
      // override this immediate clear.
      checkRequestIdRef.current += 1;
      setIsCheckingEmail(false);
      setIsEmailTaken(false);
    }

    debouncedCheckEmail(trimmedEmail);
  }, [emailValue]);

  const isEditMode = !!props.defaultValues?.customerId;

  const onSubmit = async (data: UserFormModalData) => {
    // Caller-wiring bug, not a submit failure: fail loud instead of being
    // swallowed by the try/catch below (which is for legitimate rejections
    // like an email conflict).
    if (!(isEditMode || props.onAdd)) {
      throw new Error("UserFormModal: onAdd is required in add mode");
    }
    try {
      if (isEditMode) {
        // biome-ignore lint/style/noNonNullAssertion: isEditMode guarantees customerId is set
        await props.onEdit(props.defaultValues!.customerId!, data);
      } else {
        // biome-ignore lint/style/noNonNullAssertion: guarded above
        await props.onAdd!(data);
      }
      props.onOpenChange(false);
      reset();
    } catch {
      // Keep the modal open with the current values; the caller surfaces the
      // failure via `serverError` (e.g. email already in use).
    }
  };

  const handleOpenChange = (open: boolean) => {
    props.onOpenChange(open);
    if (!open) {
      reset(props.defaultValues);
      checkRequestIdRef.current += 1;
      setIsEmailTaken(false);
      setIsCheckingEmail(false);
    }
  };

  const customerIdConflictMessage = props.customerIdServerError;

  const conflictTitle = isEditMode
    ? (props.microcopies.emailAlreadyTakenTitle ??
      props.microcopies.serverErrorTitle)
    : props.microcopies.serverErrorTitle;
  const conflictFallbackMessage = isEditMode
    ? (props.microcopies.emailAlreadyTakenMessage ??
      props.microcopies.serverErrorMessage)
    : props.microcopies.serverErrorMessage;

  const emailConflictMessage =
    props.serverError || (isEmailTaken ? conflictFallbackMessage : undefined);

  return (
    <Dialog onOpenChange={handleOpenChange} open={props.open}>
      <DialogContent className="scrollbar-modal max-h-[90vh] overflow-y-auto sm:max-w-[532px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode
              ? props.microcopies.editTitle
              : props.microcopies.addTitle}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? props.microcopies.editDescription
              : props.microcopies.addDescription}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <UserFormField
                disabled={isEditMode}
                error={errors.customerId}
                id="customerId"
                label={props.microcopies.customerIdLabel}
                registerProps={register("customerId", {
                  onBlur: () => trigger("customerId"),
                })}
                required={!isEditMode}
              />
            </div>

            {customerIdConflictMessage && (
              <Alert
                microcopies={{
                  title:
                    props.microcopies.customerIdServerErrorTitle ||
                    customerIdConflictMessage,
                  description:
                    props.microcopies.customerIdServerErrorMessage ||
                    (props.microcopies.customerIdServerErrorTitle &&
                    props.microcopies.customerIdServerErrorTitle !==
                      customerIdConflictMessage
                      ? customerIdConflictMessage
                      : undefined),
                }}
                variant="warning"
              />
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <UserFormField
                error={errors.firstName}
                id="firstName"
                label={props.microcopies.firstNameLabel}
                registerProps={register("firstName")}
                required={true}
              />

              <UserFormField
                error={errors.lastName}
                id="lastName"
                label={props.microcopies.lastNameLabel}
                registerProps={register("lastName")}
                required={true}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <UserFormField
                error={errors.email}
                id="email"
                label={props.microcopies.emailLabel}
                registerProps={register("email")}
                required={true}
                type="email"
              />

              <UserFormField
                error={errors.confirmEmail}
                id="confirmEmail"
                label={props.microcopies.confirmEmailLabel}
                registerProps={register("confirmEmail")}
                required={true}
                type="email"
              />
            </div>

            {emailConflictMessage && (
              <Alert
                microcopies={{
                  title: conflictTitle ?? emailConflictMessage,
                  description: conflictTitle ? emailConflictMessage : undefined,
                }}
                variant="warning"
              />
            )}

            {props.showRoleField !== false && (
              <div className="flex flex-col gap-2">
                <Label>{props.microcopies.roleLabel}*</Label>
                <Controller
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <RadioGroup
                      onValueChange={field.onChange}
                      options={props.roleOptions}
                      // Keep the group controlled from the first render — an
                      // undefined value here would flip RadioGroup between
                      // its uncontrolled (defaultChecked) and controlled
                      // (checked) modes once a role is picked.
                      value={field.value ?? ""}
                    />
                  )}
                />
                {errors.role && (
                  <Text color="red" variant="body-xs">
                    {errors.role.message}
                  </Text>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <Button
              disabled={
                props.isPending ||
                isSubmitting ||
                isEmailTaken ||
                isCheckingEmail
              }
              loading={isSubmitting || props.isPending}
              type="submit"
            >
              {props.microcopies.saveButtonLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
